import { motion } from "framer-motion";
import { Search, Calendar, User, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import Header from "@/components/Header";

const blogPosts = [
  {
    title: "Mastering AI Prompt Engineering: A Comprehensive Guide",
    excerpt: "Learn the fundamental principles and advanced techniques of crafting effective prompts for AI models.",
    author: "Sarah Johnson",
    date: "March 15, 2024",
    category: "Tutorials",
    imageUrl: "https://source.unsplash.com/random/800x600?ai",
    tags: ["AI", "Prompt Engineering", "Tutorial"]
  },
  {
    title: "The Future of AI Image Generation",
    excerpt: "Exploring the latest developments in AI image generation and what they mean for creators.",
    author: "Michael Chen",
    date: "March 12, 2024",
    category: "Industry Insights",
    imageUrl: "https://source.unsplash.com/random/800x600?technology",
    tags: ["AI Art", "Future Tech", "Creativity"]
  },
  {
    title: "Optimizing Prompts for Code Generation",
    excerpt: "Best practices for writing prompts that generate clean, efficient, and maintainable code.",
    author: "David Kumar",
    date: "March 10, 2024",
    category: "Development",
    imageUrl: "https://source.unsplash.com/random/800x600?code",
    tags: ["Coding", "Development", "AI"]
  }
];

const categories = [
  "All Posts",
  "Tutorials",
  "Industry Insights",
  "Development",
  "Case Studies",
  "Product Updates"
];

const Blog = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header 
        title="Blog"
        description="Insights, tutorials, and updates from the PromptPerfect team on AI prompt engineering and industry trends."
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Search and Filter Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 sm:mb-12 space-y-4 md:space-y-0 md:space-x-4">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <input
              type="text"
              placeholder="Search articles..."
              className="w-full pl-12 pr-4 py-2 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 w-full md:w-auto -mx-4 px-4 md:mx-0 md:px-0">
            {categories.map((category, index) => (
              <Button
                key={index}
                variant={index === 0 ? "default" : "outline"}
                className="whitespace-nowrap flex-shrink-0"
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {blogPosts.map((post, index) => (
            <motion.article
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-card rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col"
            >
              <div className="relative aspect-video">
                <img
                  src={post.imageUrl}
                  alt={post.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="p-4 sm:p-6 flex-1 flex flex-col">
                <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
                  <span className="text-muted-foreground flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {post.date}
                  </span>
                  <span className="text-muted-foreground flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    {post.author}
                  </span>
                </div>
                <h2 className="text-xl font-bold mb-2 text-foreground hover:text-primary transition-colors duration-200">
                  {post.title}
                </h2>
                <p className="text-muted-foreground mb-4 flex-1">{post.excerpt}</p>
                <div className="flex flex-wrap gap-2 mt-auto">
                  {post.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm bg-primary/10 text-primary"
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Load More Button */}
        <div className="flex justify-center mt-8 sm:mt-12">
          <Button variant="outline" size="lg" className="w-full sm:w-auto">
            Load More Articles
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Blog; 